import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import {
  entityRiskElementId,
  type EntityRiskScoreDocument,
} from '../../api-utils/entity_risk';
import type { ForceGraphNode } from './correlationGraph';
import EmptyState from '../../ui/EmptyState';
import ErrorState from '../../ui/ErrorState';
import Skeleton from '../../ui/Skeleton';
import { assetValueOf, describeAssetValue, type AssetValue } from '../../lib/assetValue';
import {
  BLAST_RADIUS_MAX,
  blastRadiusOf,
  riskStandings,
  riskValueOf,
  type RiskStanding,
} from '../../lib/threatIndex';
import { rowMatchesFilters, type FeedRow } from '../../data/selectors/feed';
import { formatClock, scoreCls, scoreColor, toScoreNumber } from './eriUtils';
import FeedFilterControls, { NO_FILTER_VALUE, isFiltering, type FeedFilterValue } from './FeedFilterControls';

const PAGE_SIZE_OPTIONS = [5, 10, 25] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];
const DEFAULT_PAGE_SIZE: PageSize = 10;

/**
 * The Threat Index is the merge of the Entity Risk Index and the Effective Blast Radius (client notes 11/2). Its sort lists the
 * titles of its columns, in the order the columns come, and each title is what the table is sorted by.
 */
type IndexSort = 'asset' | 'risk' | 'rank' | 'assetValue' | 'drivers' | 'activity';
type SortDir = 'asc' | 'desc';
const SORTS: { id: IndexSort; label: string; dir: SortDir }[] = [
  { id: 'asset', label: 'Asset', dir: 'asc' },
  { id: 'risk', label: 'Risk Value', dir: 'desc' },
  { id: 'rank', label: 'Risk Value Rank over 7 days', dir: 'desc' },
  { id: 'assetValue', label: 'Asset Value', dir: 'desc' },
  { id: 'drivers', label: 'Drivers', dir: 'desc' },
  { id: 'activity', label: 'Last Activity', dir: 'desc' },
];

function actionCount(row: EntityRiskScoreDocument): number {
  const fromList = Array.isArray(row.attacking_actions) ? row.attacking_actions.length : 0;
  const fromBreak = toScoreNumber(row.breakdown?.n_actions);
  return Math.max(fromList, fromBreak ?? 0);
}

function topDriver(row: EntityRiskScoreDocument): { label: string; signals: number } {
  const drivers = Array.isArray(row.top_drivers) ? row.top_drivers : [];
  const first = drivers.find((d) => String(d.label || '').trim());
  return {
    label: String(first?.label || '—'),
    signals: drivers.length || actionCount(row),
  };
}

function matchesSearch(query: string, parts: Array<string | undefined | null>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return parts.some((part) => String(part || '').toLowerCase().includes(q));
}

/** One asset as the table shows it: what the records say, and what the Threat Index makes of it. */
interface IndexRowView {
  row: EntityRiskScoreDocument;
  id: string;
  name: string;
  node: ForceGraphNode | undefined;
  value: AssetValue;
  /** 1 to 100; null when the asset has no score. */
  risk: number | null;
  standing: RiskStanding;
  /** Asset Value x Risk Value on a scale of 1 to 50; null when the asset has no score. */
  blast: number | null;
  driver: { label: string; signals: number };
  open: number;
  activityMs: number;
}

function compare(a: IndexRowView, b: IndexRowView, sort: IndexSort): number {
  switch (sort) {
    case 'asset':
      return a.name.localeCompare(b.name);
    case 'risk':
      return (a.risk ?? -1) - (b.risk ?? -1);
    case 'rank':
      return (a.standing.movement ?? -Infinity) - (b.standing.movement ?? -Infinity);
    case 'assetValue':
      return a.value.total - b.value.total;
    case 'drivers':
      return a.driver.signals - b.driver.signals;
    case 'activity':
      return (Number.isFinite(a.activityMs) ? a.activityMs : -Infinity) - (Number.isFinite(b.activityMs) ? b.activityMs : -Infinity);
    default:
      return 0;
  }
}

interface EntityRiskIndexProps {
  rows: EntityRiskScoreDocument[];
  nodes: ForceGraphNode[];
  scopeIds: Set<string> | null;
  /** Starts with "This scope only" ticked, as when the Threat Feed's View in Index leads here. */
  scopeOnlyInitially?: boolean;
  /**
   * The incidents and campaigns of the Threat Feed. With them the table has the feed's filters and time filter (client notes
   * 10/26, items 29 and 30): an asset stays when one of the incidents or campaigns that touch it passes them.
   */
  activity?: FeedRow[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onOpenEntity: (id: string, name: string) => void;
}

const EntityRiskIndex: React.FC<EntityRiskIndexProps> = ({
  rows,
  nodes,
  scopeIds,
  scopeOnlyInitially = false,
  activity,
  loading,
  error,
  onRetry,
  onOpenEntity,
}) => {
  const [sort, setSort] = useState<IndexSort>('risk');
  const [dir, setDir] = useState<SortDir>('desc');
  const [openOnly, setOpenOnly] = useState(false);
  const [scopeOnly, setScopeOnly] = useState(scopeOnlyInitially);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FeedFilterValue>(NO_FILTER_VALUE);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);

  const nodeById = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);

  // Every asset with what the Threat Index makes of it. The rank is among all the assets, whatever is filtered.
  const views = useMemo<IndexRowView[]>(() => {
    const standings = riskStandings(
      rows.map((row) => ({
        id: entityRiskElementId(row),
        score: row.score,
        previousScore: row.previous_score,
        delta: row.score_delta_7d,
      }))
    );
    return rows.map((row) => {
      const id = entityRiskElementId(row);
      const node = nodeById.get(id);
      const value = assetValueOf(node ?? { universalType: String(row.element_name || '') });
      const risk = riskValueOf(row.score);
      return {
        row,
        id,
        name: String(row.element_name || node?.label || id || '—'),
        node,
        value,
        risk,
        standing: standings.get(id) as RiskStanding,
        blast: risk == null ? null : blastRadiusOf(value.total, risk),
        driver: topDriver(row),
        open: actionCount(row),
        activityMs: Date.parse(row.computed_at || ''),
      };
    });
  }, [rows, nodeById]);

  // The incidents and campaigns that touch each asset, and the sources they come from.
  const activityByAsset = useMemo(() => {
    const byAsset = new Map<string, FeedRow[]>();
    for (const row of activity ?? []) {
      for (const chip of row.entities) byAsset.set(chip.id, [...(byAsset.get(chip.id) ?? []), row]);
    }
    return byAsset;
  }, [activity]);
  const sources = useMemo(
    () => Array.from(new Set((activity ?? []).flatMap((row) => row.sources).filter(Boolean))).sort(),
    [activity]
  );
  const filtering = isFiltering(filters);

  const filtered = useMemo(() => {
    const scoped = scopeOnly && scopeIds ? views.filter((view) => scopeIds.has(view.id)) : views;
    const openFiltered = openOnly ? scoped.filter((view) => view.open > 0) : scoped;
    const nowMs = Date.now();
    const asFeedFilters = {
      sources: filters.sources,
      severities: filters.severities,
      states: filters.states,
      types: filters.types,
      actions: filters.actions,
      period: filters.time === 'all' ? null : filters.time,
    };
    const active = filtering
      ? openFiltered.filter((view) => (activityByAsset.get(view.id) ?? []).some((row) => rowMatchesFilters(row, asFeedFilters, nowMs)))
      : openFiltered;
    const searched = active.filter((view) =>
      matchesSearch(search, [
        view.name,
        view.id,
        view.node?.label,
        view.node?.provider,
        view.node?.universalType,
        view.value.typeLabel,
        view.value.positionLabel,
        view.value.crownJewel ? 'crown jewel' : '',
        view.driver.label,
        view.row.band,
      ])
    );
    const sign = dir === 'asc' ? 1 : -1;
    return [...searched].sort((a, b) => sign * compare(a, b, sort) || a.name.localeCompare(b.name));
  }, [views, scopeIds, scopeOnly, openOnly, sort, dir, search, filters, filtering, activityByAsset]);

  // The Blast Radius of the table is the largest one among the assets it lists.
  const blastInView = useMemo(() => {
    const values = filtered.map((view) => view.blast).filter((value): value is number => value != null);
    return values.length > 0 ? Math.max(...values) : null;
  }, [filtered]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);

  useEffect(() => {
    setPage(0);
  }, [sort, dir, scopeOnly, openOnly, search, filters, scopeIds, pageSize]);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const visible = useMemo(() => {
    const start = safePage * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  const total = scopeIds ? views.filter((view) => scopeIds.has(view.id)).length : views.length;
  const rangeStart = filtered.length === 0 ? 0 : safePage * pageSize + 1;
  const rangeEnd = Math.min(filtered.length, (safePage + 1) * pageSize);

  const chooseSort = (next: IndexSort) => {
    setSort(next);
    setDir(SORTS.find((item) => item.id === next)?.dir ?? 'desc');
  };

  return (
    <div id="c3Eri">
      <div className="eri-titlebar">
        <h3 className="eri-title">Threat Index</h3>
        <div
          className="eri-blast"
          title={`Asset Value × Risk Value of the most exposed asset listed, on a scale of 1 to ${BLAST_RADIUS_MAX}`}
        >
          <span className="eri-blast-label">Blast Radius</span>
          <b className="eri-blast-value">{blastInView ?? '—'}</b>
          <span className="eri-blast-max">of {BLAST_RADIUS_MAX}</span>
        </div>
      </div>
      {activity ? (
        <div className="eri-filters" role="group" aria-label="Filters">
          <FeedFilterControls
            sources={sources}
            value={filters}
            onChange={(patch) => setFilters((current) => ({ ...current, ...patch }))}
          />
          {filtering ? (
            <button type="button" className="c3-btn c3-btn-text c3-btn-sm" onClick={() => setFilters(NO_FILTER_VALUE)}>
              Clear filters
            </button>
          ) : null}
          <span className="eri-filters-note">The filters apply to the incidents and campaigns on each asset</span>
        </div>
      ) : null}
      <div className="eri-toolbar">
        <label className="eri-search">
          <span className="sr-only">Search</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search assets…"
          />
        </label>
        <div className="eri-sort">
          <label className="eri-sort-by">
            <span>Sort</span>
            <select value={sort} aria-label="Sort by" onChange={(e) => chooseSort(e.target.value as IndexSort)}>
              {SORTS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="c3-pill eri-pager-btn"
            aria-label={dir === 'asc' ? 'Sorted ascending, switch to descending' : 'Sorted descending, switch to ascending'}
            onClick={() => setDir((current) => (current === 'asc' ? 'desc' : 'asc'))}
          >
            {dir === 'asc' ? <ArrowUp className="w-4 h-4" aria-hidden /> : <ArrowDown className="w-4 h-4" aria-hidden />}
          </button>
        </div>
        <label className="eri-filter">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(e) => setOpenOnly(e.target.checked)}
          />
          Open incident
        </label>
        {scopeIds && (
          <label className="eri-filter">
            <input
              type="checkbox"
              checked={scopeOnly}
              onChange={(e) => setScopeOnly(e.target.checked)}
            />
            This scope only
          </label>
        )}
        <div className="eri-pager">
          <button
            type="button"
            className="c3-pill eri-pager-btn"
            disabled={safePage <= 0}
            aria-label="Previous page"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="eri-count">
            {rangeStart}–{rangeEnd} of {filtered.length}
            {scopeIds ? ` · ${total} in scope` : ''}
          </span>
          <label className="eri-page-size">
            <span className="sr-only">Rows per page</span>
            <select
              value={pageSize}
              aria-label="Rows per page"
              onChange={(e) => setPageSize(Number(e.target.value) as PageSize)}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="c3-pill eri-pager-btn"
            disabled={safePage >= pageCount - 1}
            aria-label="Next page"
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="eri-head">
        {SORTS.map((item) => (
          <div key={item.id}>{item.label}</div>
        ))}
      </div>
      {loading ? (
        <div className="mt-3">
          <Skeleton lines={4} label="Loading the Threat Index…" />
        </div>
      ) : error ? (
        <ErrorState title={error} onRetry={onRetry}>
          Nothing was changed. Try again in a moment.
        </ErrorState>
      ) : rows.length === 0 ? (
        <EmptyState title="No risk scores yet">Assets appear here once the system has scored them.</EmptyState>
      ) : filtered.length === 0 ? (
        <EmptyState title="No assets match these filters">Change a filter to see more.</EmptyState>
      ) : (
        visible.map((view) => {
          const { row, id, value, risk, standing, blast, driver } = view;
          const typeLine = [value.typeLabel, value.crownJewel ? 'Crown jewel' : '', value.positionLabel ?? '']
            .filter(Boolean)
            .join(' · ');
          return (
            <button
              key={row._id || id}
              type="button"
              className="eri-row"
              onClick={() => onOpenEntity(id, view.name)}
            >
              <div>
                <div className="eri-name">{view.name}</div>
                <div className="eri-type">{typeLine}</div>
              </div>
              <div
                className="eri-risk"
                data-label="Risk Value"
                title={
                  risk != null && blast != null
                    ? `Risk Value ${risk} × Asset Value ${value.total} = Blast Radius ${blast} of ${BLAST_RADIUS_MAX}`
                    : 'No risk score yet'
                }
              >
                <span className={`eri-score ${risk != null ? scoreCls(risk) : ''}`}>{risk ?? '—'}</span>
                <span className="eri-bar">
                  <i
                    style={{
                      width: `${risk ?? 0}%`,
                      background: scoreColor(risk ?? 0),
                    }}
                  />
                </span>
              </div>
              <div
                className="eri-rank"
                data-label="Risk Value Rank over 7 days"
                title={
                  standing.previousRank == null
                    ? `Rank ${standing.rank} now; the records say nothing of seven days ago`
                    : `Rank ${standing.rank} now, ${standing.previousRank} seven days ago`
                }
              >
                <span className="eri-rank-now">#{standing.rank}</span>
                <span className={`eri-trend ${standing.movement == null ? '' : standing.movement > 0 ? 'tr-up' : standing.movement < 0 ? 'tr-dn' : ''}`}>
                  {standing.movement == null ? (
                    '—'
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      {standing.movement > 0 ? (
                        <TrendingUp size={12} strokeWidth={2} aria-hidden />
                      ) : standing.movement < 0 ? (
                        <TrendingDown size={12} strokeWidth={2} aria-hidden />
                      ) : (
                        <Minus size={12} strokeWidth={2} aria-hidden />
                      )}
                      {Math.abs(standing.movement)}
                    </span>
                  )}
                </span>
              </div>
              <div className="eri-assetvalue" data-label="Asset Value" title={describeAssetValue(value)}>
                {value.total}
              </div>
              <div className="eri-driver" data-label="Drivers">
                <div className="eri-driver-label">{driver.label}</div>
                <div className="eri-type">
                  {driver.signals} signal{driver.signals === 1 ? '' : 's'}
                </div>
              </div>
              <div className="eri-rel" data-label="Last Activity">{formatClock(row.computed_at)}</div>
            </button>
          );
        })
      )}
    </div>
  );
};

export default EntityRiskIndex;
